import { useDataQuery } from "@Libs/DataLayer/hooks/useDataQuery";
import { getCurrentCountry } from "@config";
import {
  fetchCategoryTree,
  findPCategory,
  findSCategories,
  ICategoryNode,
  ICategorySelection,
  resolveCategoryKey,
} from "../services/categories";

export const CATEGORY_TREE_QUERY_KEY = "taskCreationCategoryTree";

export const useCategoryTree = () => {
  const country = getCurrentCountry();
  const { data, isLoading, error } = useDataQuery(
    // The country is part of the key, not just the request: the tree it returns
    // is country-specific, so a cache shared across countries would serve the
    // wrong one.
    [CATEGORY_TREE_QUERY_KEY, country],
    // Wrapped rather than passed by reference — react-query calls `queryFn`
    // with its own context object, which would land in the `country` parameter
    // and be serialised into the URL.
    () => fetchCategoryTree(country),
    { staleTime: 10 * 60 * 1000 } // 10min — the tree barely changes.
  );

  return { tree: data, isLoading, error };
};

interface IUsePCategoryResult {
  pCategory: ICategoryNode | undefined;
  sCategories: ICategoryNode[];
  isLoading: boolean;
  error: unknown;
}

export const usePCategory = (pCategoryId?: number): IUsePCategoryResult => {
  const { tree, isLoading, error } = useCategoryTree();

  if (!tree || pCategoryId === undefined) {
    return { pCategory: undefined, sCategories: [], isLoading, error };
  }

  return {
    pCategory: findPCategory(tree, pCategoryId),
    sCategories: findSCategories(tree, pCategoryId),
    isLoading,
    error,
  };
};

interface IUseCategoryKeyResult {
  keyName: string | undefined;
  isLoading: boolean;
  error: unknown;
}

/**
 * The category `keyName` for a `{ p_category, s_category }` pair out of the
 * URL — `undefined` while loading, on error, or when neither id resolves, so a
 * caller renders its own copy rather than a hole.
 */
export const useCategoryKey = (
  selection: ICategorySelection
): IUseCategoryKeyResult => {
  const { tree, isLoading, error } = useCategoryTree();

  return {
    keyName: tree ? resolveCategoryKey(tree, selection) : undefined,
    isLoading,
    error,
  };
};
